import { Smartphone } from 'lucide-react';
import QRCode from 'qrcode';
import { type ReactNode, useEffect, useState } from 'react';

// Enforces Workflow Settings → Options → "Allow desktop access".
//
// When a workflow turns desktop access off, the applicant run is restricted to
// touch devices: verification depends on the camera (document capture, selfie,
// liveness) and a laptop webcam is materially worse, so the workflow owner can
// insist on a phone. Instead of a dead end, the desktop visitor gets the same
// link as a QR code to carry across.
//
// Applies to the PUBLIC applicant screens only. The internal fill screen
// (staff running a session from the console) is deliberately never gated.

// Input capability, not a user-agent list: a device with no hover and a coarse
// pointer is being driven by a finger. This keeps working as new devices appear,
// where a UA list rots.
const TOUCH_QUERY = '(hover: none) and (pointer: coarse)';

/**
 * True when this looks like a touch-primary device (phone / tablet).
 *
 * Re-evaluates on change, so rotating the device or toggling device emulation in
 * devtools doesn't leave a stale verdict.
 */
export function useIsTouchDevice(): boolean {
  const [isTouch, setIsTouch] = useState(() =>
    typeof window === 'undefined' ? true : window.matchMedia(TOUCH_QUERY).matches,
  );

  useEffect(() => {
    const mql = window.matchMedia(TOUCH_QUERY);
    const onChange = (e: MediaQueryListEvent) => setIsTouch(e.matches);
    mql.addEventListener('change', onChange);
    setIsTouch(mql.matches);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  return isTouch;
}

/**
 * Renders `children` when the run is allowed on this device, otherwise the
 * "continue on your phone" screen.
 *
 * `allowDesktop` defaults to true so a session that hasn't reported its options
 * (older API, or a response where options are absent) is never blocked.
 */
export function MobileOnlyGate({
  allowDesktop = true,
  children,
}: {
  allowDesktop?: boolean;
  children: ReactNode;
}) {
  const isTouch = useIsTouchDevice();
  if (allowDesktop || isTouch) return <>{children}</>;
  return <DesktopBlockedScreen />;
}

function DesktopBlockedScreen() {
  const url = typeof window === 'undefined' ? '' : window.location.href;
  const [qr, setQr] = useState<string | null>(null);

  useEffect(() => {
    if (!url) return;
    let active = true;
    QRCode.toDataURL(url, { width: 320, margin: 1 })
      .then((dataUrl) => {
        if (active) setQr(dataUrl);
      })
      // A failed QR is not a failed page — the link text below still works.
      .catch(() => setQr(null));
    return () => {
      active = false;
    };
  }, [url]);

  return (
    <div className="flex min-h-dvh items-center justify-center bg-[#F1F5FB] px-6 py-10">
      <div className="flex w-full max-w-md flex-col items-center gap-5 rounded-2xl border border-grayEF bg-white px-8 py-10 text-center shadow-elevation-1">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-blue-tint text-primary">
          <Smartphone size={26} />
        </span>
        <div className="flex flex-col gap-2">
          <h1 className="m-0 text-xl font-bold" style={{ color: 'var(--brand-heading, #041A3A)' }}>
            Continue on your phone
          </h1>
          <p
            className="m-0 text-sm leading-relaxed"
            style={{ color: 'var(--brand-body, #666666)' }}
          >
            This verification needs a mobile camera, so it can only be completed on a phone. Scan
            the code below to carry on there.
          </p>
        </div>

        {qr && (
          <img
            src={qr}
            alt="QR code linking to this verification"
            className="h-40 w-40 rounded-lg border border-grayEF"
          />
        )}

        <div className="flex w-full flex-col gap-1.5">
          <span className="text-xs font-medium text-gray30">Or open this link on your phone</span>
          <p className="m-0 break-all rounded-lg bg-[#F4F6FD] px-3 py-2 text-xs text-gray66">
            {url}
          </p>
        </div>
      </div>
    </div>
  );
}
